import { EncryptedPayload } from './types';

/**
 * CK Share Cryptographic Engine
 * Zero-dependency Web Crypto AES-GCM 256-bit E2EE with PBKDF2 key derivation
 */

const PBKDF2_ITERATIONS = 100000;
const AES_KEY_LENGTH = 256;
const IV_BYTE_LENGTH = 12;
const SALT_BYTE_LENGTH = 16;

/**
 * ArrayBuffer to Base64 string
 */
export function bufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Base64 string to Uint8Array
 */
export function base64ToBuffer(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Derive a 256-bit AES-GCM CryptoKey from a password and salt using PBKDF2
 */
export async function deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const passwordKey = await crypto.subtle.importKey(
    'raw',
    enc.encode(password),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt as unknown as BufferSource,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256'
    },
    passwordKey,
    {
      name: 'AES-GCM',
      length: AES_KEY_LENGTH
    },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypt plaintext string using AES-GCM-256 with key derived from room access code
 */
export async function encryptPayload(plaintext: string, accessCode: string): Promise<EncryptedPayload> {
  if (!accessCode) {
    throw new Error('Access code is required for encryption');
  }

  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTE_LENGTH));
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTE_LENGTH));
  const key = await deriveKey(accessCode, salt);

  const enc = new TextEncoder();
  const encodedPlaintext = enc.encode(plaintext);

  const ciphertextBuffer = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv as unknown as BufferSource
    },
    key,
    encodedPlaintext
  );

  return {
    iv: bufferToBase64(iv),
    salt: bufferToBase64(salt),
    ciphertext: bufferToBase64(ciphertextBuffer)
  };
}

/**
 * Decrypt AES-GCM-256 encrypted bundle using room access code
 * Throws an error if password is incorrect or ciphertext has been tampered with.
 */
export async function decryptPayload(bundle: EncryptedPayload, accessCode: string): Promise<string> {
  if (!accessCode) {
    throw new Error('Access code is required for decryption');
  }
  if (!bundle.iv || !bundle.salt || !bundle.ciphertext) {
    throw new Error('Invalid encrypted payload bundle format');
  }

  const iv = base64ToBuffer(bundle.iv);
  const salt = base64ToBuffer(bundle.salt);
  const ciphertext = base64ToBuffer(bundle.ciphertext);

  const key = await deriveKey(accessCode, salt);

  try {
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv as unknown as BufferSource
      },
      key,
      ciphertext as unknown as BufferSource
    );

    const dec = new TextDecoder();
    return dec.decode(decryptedBuffer);
  } catch (err) {
    throw new Error('Decryption failed: Invalid password or corrupted payload');
  }
}

/**
 * Generate a cryptographically secure random room code (e.g. "CK-7X9K2M" -> 1+ Billion combinations)
 */
export function generateRoomCode(): string {
  const chars = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
  const array = new Uint8Array(6);
  crypto.getRandomValues(array);
  let code = '';
  for (let i = 0; i < array.length; i++) {
    code += chars[array[i] % chars.length];
  }
  return `CK-${code}`;
}

/**
 * Generate a cryptographically secure default access code (8-character alphanumeric)
 */
export function generateAccessCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const array = new Uint8Array(8);
  crypto.getRandomValues(array);
  let code = '';
  for (let i = 0; i < array.length; i++) {
    code += chars[array[i] % chars.length];
  }
  return code;
}
